import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AIQuotaContext, type AIQuotaSnapshot, type AIQuotaValue } from 'core/hooks/useAIQuota';

import { getQuotaUsage } from './getQuotaUsage';

type QuotaService = {
  getQuota: (options?: { signal?: AbortSignal }) => Promise<AIQuotaSnapshot | null>;
};

type AIChatQuotaProviderProps = {
  chatService: QuotaService;
  children: React.ReactNode;
};

/**
 * Mounted by ChatbotWidgetCore (students with AI) and by each teacher tools screen. It reads on mount,
 * so the quota is known before the student opens the chat and as soon as the teacher enters a screen.
 */
function AIChatQuotaProvider({ chatService, children }: AIChatQuotaProviderProps): JSX.Element {
  const [snapshot, setSnapshot] = useState<AIQuotaSnapshot | null>(null);
  const snapshotRef = useRef<AIQuotaSnapshot | null>(null);
  const isUnmountedRef = useRef(false);
  const pendingReadsRef = useRef<Set<AbortController>>(new Set());

  const storeSnapshot = useCallback((nextSnapshot: AIQuotaSnapshot | null): void => {
    snapshotRef.current = nextSnapshot;
    setSnapshot(nextSnapshot);
  }, []);

  const refreshQuota = useCallback(async (): Promise<void> => {
    if (isUnmountedRef.current) {
      return;
    }
    const controller = new AbortController();
    pendingReadsRef.current.add(controller);
    try {
      const nextSnapshot = await chatService.getQuota({ signal: controller.signal });
      if (!controller.signal.aborted) {
        storeSnapshot(nextSnapshot);
      }
    } catch {
      // chatService already logs it; keeping the last good value beats hiding the counter.
    } finally {
      pendingReadsRef.current.delete(controller);
    }
  }, [chatService, storeSnapshot]);

  const reportQuotaExhausted = useCallback((): void => {
    if (isUnmountedRef.current) {
      return;
    }
    // Without a previous read the limit is unknown, but the backend has just enforced one: 0 of 0 is exhausted.
    const quota = snapshotRef.current?.quota ?? 0;
    storeSnapshot({ quota, consumed: Math.max(snapshotRef.current?.consumed ?? 0, quota) });
    refreshQuota();
  }, [refreshQuota, storeSnapshot]);

  const reportTurnQuota = useCallback(
    (turnSnapshot: AIQuotaSnapshot | null): void => {
      if (isUnmountedRef.current) {
        return;
      }
      if (turnSnapshot) {
        storeSnapshot(turnSnapshot);
        return;
      }
      // Without a quota already read (204 or a failed read), re-reading would repeat it on every turn.
      if (snapshotRef.current) {
        refreshQuota();
      }
    },
    [refreshQuota, storeSnapshot]
  );

  useEffect(() => {
    isUnmountedRef.current = false;
    const pendingReads = pendingReadsRef.current;
    return () => {
      isUnmountedRef.current = true;
      pendingReads.forEach((controller) => controller.abort());
    };
  }, []);

  // After the effect above: on a remount, isUnmountedRef must be false again before reading.
  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  const value = useMemo<AIQuotaValue>(
    () => ({ refreshQuota, reportQuotaExhausted, reportTurnQuota, usage: getQuotaUsage(snapshot) }),
    [refreshQuota, reportQuotaExhausted, reportTurnQuota, snapshot]
  );

  return <AIQuotaContext.Provider value={value}>{children}</AIQuotaContext.Provider>;
}

export default AIChatQuotaProvider;
